/**
 * Browser-safe UUID helper for LAN HTTP (non-secure-context) environments.
 *
 * Android browsers (Samsung Internet, older Chrome) often lack
 * `crypto.randomUUID()` outside HTTPS. Never throw — catalogue / OCR must keep working.
 */

function byteToHex(byte: number): string {
  return byte.toString(16).padStart(2, '0');
}

/** RFC 4122 Version 4 UUID from 16 random bytes. */
function uuidFromBytes(bytes: Uint8Array): string {
  // Set version (4) and variant (10xx).
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, byteToHex).join('');
  return (
    `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
    `${hex.slice(16, 20)}-${hex.slice(20, 32)}`
  );
}

function randomBytesViaMath(length: number): Uint8Array {
  const out = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    out[i] = Math.floor(Math.random() * 256);
  }
  return out;
}

/**
 * Create a client-side identifier suitable for draft / operation IDs.
 * Prefer `crypto.randomUUID`, then `getRandomValues`, then a non-throwing fallback.
 */
export function createClientId(): string {
  try {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function') {
      return c.randomUUID();
    }
  } catch {
    /* continue */
  }

  try {
    const c = globalThis.crypto;
    if (c && typeof c.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      c.getRandomValues(bytes);
      return uuidFromBytes(bytes);
    }
  } catch {
    /* continue */
  }

  // Last resort: never throws. Not cryptographically strong; fine for local IDs.
  const bytes = randomBytesViaMath(16);
  const time = Date.now();
  bytes[0] = (time >>> 24) & 0xff;
  bytes[1] = (time >>> 16) & 0xff;
  bytes[2] = (time >>> 8) & 0xff;
  bytes[3] = time & 0xff;
  return uuidFromBytes(bytes);
}
